/**
 * BackgroundChecks — Admin panel for Checkr background screening (Jun 2026).
 * Graceful degradation: shows an amber "needs API key" notice until
 * CHECKR_API_KEY is configured; the roster + actions activate automatically.
 */
import React, { useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import { ShieldCheck, AlertTriangle, RefreshCw, Search, Send, KeyRound } from 'lucide-react';

const STATUS_STYLES = {
  not_requested: 'bg-slate-100 text-slate-600',
  queued: 'bg-violet-100 text-violet-700',
  invited: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  complete: 'bg-emerald-100 text-emerald-700',
  suspended: 'bg-red-100 text-red-600',
  dispute: 'bg-red-100 text-red-600',
};

const ASSESSMENT_STYLES = {
  clear: 'bg-emerald-100 text-emerald-700',
  consider: 'bg-amber-100 text-amber-800',
};

export default function BackgroundChecks({ role = 'admin' }) {
  const isAdmin = role === 'admin';
  const [status, setStatus] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState('');
  const [recheckMonths, setRecheckMonths] = useState('12');

  const saveRecheck = async (enabled, months) => {
    setBusy('recheck');
    try {
      const r = await apiCall('/api/checkr/recheck-settings', {
        method: 'POST',
        body: JSON.stringify({ enabled, months: parseInt(months, 10) || 12 }),
      });
      setStatus((s) => ({ ...s, recheck: r.recheck }));
      toast.success(enabled
        ? `Re-checks ON — checks expire after ${r.recheck.months} months and auto-requeue daily`
        : 'Periodic re-checks OFF');
    } catch (e) {
      toast.error(e.message || 'Failed to save re-check policy');
    } finally {
      setBusy(null);
    }
  };

  const toggleGate = async () => {
    const next = !status?.gate_enabled;
    setBusy('gate');
    try {
      await apiCall('/api/checkr/gate', { method: 'POST', body: JSON.stringify({ enabled: next }) });
      setStatus((s) => ({ ...s, gate_enabled: next }));
      toast.success(next
        ? 'Eligibility gate ON — only workers with a clear background check can accept shifts'
        : 'Eligibility gate OFF — all compliant workers can accept shifts');
    } catch (e) {
      toast.error(e.message || 'Failed to update gate');
    } finally {
      setBusy(null);
    }
  };

  const load = async () => {
    try {
      const [s, list] = await Promise.all([
        apiCall('/api/checkr/status'),
        apiCall('/api/checkr/checks'),
      ]);
      setStatus(s);
      if (s?.recheck?.months) setRecheckMonths(String(s.recheck.months));
      setWorkers(list.workers || []);
      setCounts(list.counts || {});
    } catch (e) {
      toast.error(e.message || 'Failed to load background checks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useLiveRefresh(['users', 'checkr'], () => load());

  const initiate = async (w) => {
    setBusy(w.worker_id);
    try {
      await apiCall(`/api/checkr/workers/${w.worker_id}/background-check`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      toast.success(`Background check invitation emailed to ${w.full_name || w.email}`);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to start background check');
    } finally {
      setBusy(null);
    }
  };

  const syncOne = async (w) => {
    setBusy(w.worker_id);
    try {
      await apiCall(`/api/checkr/workers/${w.worker_id}/background-check?sync=true`);
      toast.success('Status synced from Checkr');
      load();
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setBusy(null);
    }
  };

  const filtered = workers.filter((w) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (w.full_name || '').toLowerCase().includes(q) || (w.email || '').toLowerCase().includes(q);
  });

  if (loading) {
    return <div className="py-16 text-center text-slate-400" data-testid="checkr-loading"><RefreshCw className="w-6 h-6 mx-auto animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="background-checks-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600" /> Background Checks
          </h2>
          <p className="text-sm text-slate-500">
            {isAdmin
              ? 'Checkr-powered screening for every worker on the platform — workers complete consent & PII on Checkr\u2019s secure hosted page.'
              : role === 'agent'
                ? 'Run Checkr background checks for your agency roster — workers complete consent & PII on Checkr\u2019s secure hosted page.'
                : 'Run Checkr background checks for workers connected to you (applicants, assigned & preferred) — workers complete consent & PII on Checkr\u2019s secure hosted page.'}
          </p>
        </div>
        <button onClick={load} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-1.5" data-testid="checkr-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {!status?.supported && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3" data-testid="checkr-unconfigured-notice">
          <KeyRound className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-900">Checkr API key required</p>
            <p className="text-sm text-amber-800 mt-1">
              Sign up at <span className="font-mono">checkr.com</span> → Dashboard → Account Settings → Developer Settings → create a <strong>Secret key</strong> (request a free <strong>staging</strong> account first for testing).
              Then add <span className="font-mono">CHECKR_API_KEY</span> and <span className="font-mono">CHECKR_WEBHOOK_SECRET</span> to the backend environment — this panel activates automatically.
            </p>
          </div>
        </div>
      )}

      {isAdmin && status?.supported && (
        <div className="flex items-center gap-2 text-xs" data-testid="checkr-mode-badge">
          <span className={`px-2.5 py-1 rounded-full font-semibold ${status.mode === 'live' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
            Checkr: {status.mode}
          </span>
          <span className={`px-2.5 py-1 rounded-full font-semibold ${status.webhook_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            Webhook {status.webhook_configured ? 'configured' : 'secret missing'}
          </span>
          <span className="px-2.5 py-1 rounded-full font-semibold bg-slate-100 text-slate-600">Package: {status.package}</span>
        </div>
      )}

      {isAdmin && (
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-4" data-testid="checkr-gate-card">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">Require clear background check to accept shifts</p>
          <p className="text-xs text-slate-500 mt-0.5">
            When ON, workers without a <span className="font-semibold text-emerald-600">clear</span> Checkr assessment are blocked from picking up shifts (agency assignments included). Manual compliance overrides still apply.
          </p>
        </div>
        <button
          onClick={toggleGate}
          disabled={busy === 'gate'}
          role="switch"
          aria-checked={status?.gate_enabled || false}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${status?.gate_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
          data-testid="checkr-gate-toggle"
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${status?.gate_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
      )}

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4" data-testid="checkr-recheck-card">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-900">Periodic re-checks</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Checks older than the validity window expire and are automatically re-queued daily. Expired workers lose their verified badge (and shift eligibility if the gate is on).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="number" min="1" max="36"
                value={recheckMonths}
                onChange={(e) => setRecheckMonths(e.target.value)}
                onBlur={() => { if (status?.recheck?.enabled) saveRecheck(true, recheckMonths); }}
                className="w-28 px-2.5 py-1.5 pr-16 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                data-testid="recheck-months-input"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">months</span>
            </div>
            <button
              onClick={() => saveRecheck(!status?.recheck?.enabled, recheckMonths)}
              disabled={busy === 'recheck'}
              role="switch"
              aria-checked={status?.recheck?.enabled || false}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${status?.recheck?.enabled ? 'bg-indigo-500' : 'bg-slate-300'}`}
              data-testid="recheck-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${status?.recheck?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      )}

      {isAdmin && status?.supported && (counts.queued || 0) > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 flex items-center justify-between gap-4" data-testid="checkr-queue-card">
          <p className="text-sm text-violet-900 font-medium">
            {counts.queued} worker{counts.queued === 1 ? '' : 's'} queued for automatic checks (approved before Checkr was configured).
          </p>
          <button
            onClick={async () => {
              setBusy('queue');
              try {
                const r = await apiCall('/api/checkr/process-queue', { method: 'POST' });
                toast.success(`Started ${r.started} checks${r.failed?.length ? `, ${r.failed.length} failed` : ''}`);
                load();
              } catch (e) {
                toast.error(e.message || 'Queue processing failed');
              } finally {
                setBusy(null);
              }
            }}
            disabled={busy === 'queue'}
            className="px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
            data-testid="checkr-process-queue-btn"
          >
            {busy === 'queue' ? 'Running…' : 'Run queued checks'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {['not_requested', 'queued', 'invited', 'pending', 'complete', 'suspended'].map((k) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl p-3 text-center" data-testid={`checkr-count-${k}`}>
            <p className="text-2xl font-bold text-slate-900">{counts[k] || 0}</p>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{k.replace('_', ' ')}</p>
          </div>
        ))}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search workers by name or email"
          className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500"
          data-testid="checkr-search-input"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="checkr-workers-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2.5">Worker</th>
                <th className="text-center px-4 py-2.5">Status</th>
                <th className="text-center px-4 py-2.5">Assessment</th>
                <th className="text-right px-4 py-2.5">Check Age</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No workers found.</td></tr>
              )}
              {filtered.map((w) => (
                <tr key={w.worker_id} className="border-t border-slate-100" data-testid={`checkr-row-${w.worker_id}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-slate-800 truncate max-w-[220px]">{w.full_name || '—'}</p>
                    <p className="text-xs text-slate-500 truncate max-w-[220px]">{w.email}</p>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[w.checkr_status] || 'bg-slate-100 text-slate-600'}`} data-testid={`checkr-status-${w.worker_id}`}>
                      {(w.checkr_status || 'not_requested').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {w.checkr_assessment ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${ASSESSMENT_STYLES[w.checkr_assessment] || 'bg-slate-100 text-slate-600'}`}>
                        {w.checkr_assessment}
                        {w.checkr_assessment === 'consider' && <AlertTriangle className="w-3 h-3 inline ml-1 -mt-0.5" />}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs">
                    {w.check_expired ? (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-bold" data-testid={`check-expired-${w.worker_id}`}>EXPIRED</span>
                    ) : w.check_expires_at ? (
                      <span className="text-slate-500" data-testid={`check-expires-${w.worker_id}`} title={`Completed ${w.checkr_completed_at ? new Date(w.checkr_completed_at).toLocaleDateString() : ''}`}>
                        expires {new Date(w.check_expires_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-slate-500">{w.checkr_updated_at ? new Date(w.checkr_updated_at).toLocaleDateString() : '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1.5">
                      {(w.checkr_status === 'not_requested' || w.checkr_status === 'invited' || w.checkr_status === 'queued' || w.check_expired) && (
                        <button
                          onClick={() => initiate(w)}
                          disabled={!status?.supported || busy === w.worker_id}
                          title={status?.supported ? 'Send Checkr invitation email' : 'Requires Checkr API key'}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40"
                          data-testid={`checkr-initiate-${w.worker_id}`}
                        >
                          <Send className="w-3 h-3" /> {w.checkr_status === 'invited' ? 'Resend' : 'Run check'}
                        </button>
                      )}
                      {w.checkr_report_id && (
                        <button
                          onClick={() => syncOne(w)}
                          disabled={busy === w.worker_id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 disabled:opacity-40"
                          data-testid={`checkr-sync-${w.worker_id}`}
                        >
                          <RefreshCw className="w-3 h-3" /> Sync
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
